import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { LoadingOutlined } from '@ant-design/icons';
import Pficon from 'components/Pficon';
import { QA_ACTION_TYPES } from 'containers/QuestionnairePage/constants';
import useMutateFavoriteQuestion from '../../hooks/useFavoriteQuestion';
import { updateQuestionFavoriteState } from '../../../actions';
import messages from './messages';

// This component currently uses optimistic updates. Meaning that it will expect the operation to succeed
export default function FavoriteCheckbox({
  // we need the survey id since refIds are not unique in general but only unique per survey
  questionRefId,
  surveyId,
  defaultChecked = false,
  className,
  style = {},
  disabled,
  type = QA_ACTION_TYPES.BUTTON,
  onToggle,
}) {
  const isListItem = type === QA_ACTION_TYPES.BUTTON_WITH_TEXT;
  const [isChecked, setChecked] = useState(defaultChecked);
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setChecked(defaultChecked);
  }, [defaultChecked]);

  const updateQuestionState = useCallback(
    (payload) => {
      // we need to keep the state for when the component unmounts and re-mounts
      dispatch(updateQuestionFavoriteState(payload));
    },
    [dispatch],
  );
  const { mutate } = useMutateFavoriteQuestion({
    onSuccess: ({ isFavorite }) => {
      updateQuestionState({ questionRefId, isFavorite });
    },
    onSettled: () => {
      setLoading(false);
    },
  });
  const onFavoriteQuestion = useCallback(
    (e) => {
      e.stopPropagation();
      onToggle?.();
      setLoading(true);
      const payload = {
        favorite: !isChecked,
      };
      if (questionRefId && surveyId) {
        payload.questionRefId = questionRefId;
        // surveyId must be a number
        payload.surveyId = parseInt(surveyId, 10);
      } else {
        throw new Error('Missing questionRefId & surveyId');
      }
      mutate({ ...payload });
      setChecked(!isChecked);
    },
    [mutate, setChecked, isChecked, questionRefId, surveyId, onToggle],
  );

  const heartIconStyle = isListItem
    ? { fontSize: 16, width: 16, textAlign: 'center' }
    : {};

  return (
    <Button
      onClick={onFavoriteQuestion}
      className={classNames(
        className,
        'outline-blue',
        'border-none',
        'margin-none',
        'background-transparent',
        'background-transparent-hover',

        'box-shadow-none',
        {
          'padding-xxs': !isListItem,
          'padding-horizontal-xxs': isListItem,
          'text-align-left': isListItem,
          'w-full': isListItem,
          'h-auto': !isListItem,
          'color-grey-7': !isListItem && (!isChecked || disabled),
          'color-grey-10':
            (isListItem && (!isChecked || disabled)) ||
            (isChecked && !isListItem),
          'color-grey-10-hover': !disabled && !isListItem,
          'color-blue-6-hover': !disabled && isListItem,
        },
      )}
      style={{
        fontSize: isListItem ? undefined : 16,
        lineHeight: isListItem ? '31px' : 1,
        ...style,
      }}
      disabled={isLoading || disabled}
    >
      {isListItem ? (
        <span className="flex flex-items-center w-full">
          {isLoading && <LoadingOutlined className="margin-right-xxs" />}
          {!isLoading && !isChecked && (
            <Pficon
              type="hearto"
              className="margin-right-xxs text-size-secondary"
              style={heartIconStyle}
            />
          )}
          {!isLoading && isChecked && (
            <Pficon
              type="heart"
              className="margin-right-xxs text-size-secondary color-grey-10"
              style={heartIconStyle}
            />
          )}
          <FormattedMessage
            {...(isChecked
              ? messages.removeFromFavorites
              : messages.addToFavorites)}
          />
        </span>
      ) : (
        <Tooltip
          title={
            isChecked ? (
              <FormattedMessage {...messages.removeFromFavorites} />
            ) : (
              <FormattedMessage {...messages.addToFavorites} />
            )
          }
          arrowPointAtCenter
          overlayClassName="tooltip-light"
          placement="bottomRight"
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <span>
            {isLoading && <LoadingOutlined />}
            {!isLoading && !isChecked && <Pficon type="hearto" />}
            {!isLoading && isChecked && <Pficon type="heart" />}
          </span>
        </Tooltip>
      )}
    </Button>
  );
}

FavoriteCheckbox.propTypes = {
  questionRefId: PropTypes.string,
  surveyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  defaultChecked: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  onToggle: PropTypes.func,
};
